import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus, ArrowRight } from 'lucide-react';

interface ButtonType {
  value: string;
  label: string;
  sublabel?: string;
  icon: any;
  maxCount: number;
  disabled?: boolean;
  disabledReason?: string;
}

interface AddButtonDropdownProps {
  availableTypes: ButtonType[];
  onSelect: (type: string) => void;
  disabled?: boolean;
  testId?: string;
}

export const AddButtonDropdown = ({ availableTypes, onSelect, disabled, testId }: AddButtonDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredValue, setHoveredValue] = useState<string | null>(null);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);

      // Calculate if we should drop up or down
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        // Threshold of 400px (buttons dropdown is taller)
        setDropUp(spaceBelow < 400 && spaceAbove > spaceBelow);
      }
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const customTypes = availableTypes.filter(t => t.value === 'quick_reply');
  const ctaTypes = availableTypes.filter(t => t.value !== 'quick_reply');

  const handleSelect = (type: string) => {
    onSelect(type);
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!disabled && availableTypes.length > 0 && !availableTypes.every(t => t.disabled)) {
      setIsOpen(!isOpen);
    }
  };

  const isAllDisabled = availableTypes.every(t => t.disabled);

  if (availableTypes.length === 0) {
    return (
      <button
        type="button"
        disabled
        className="px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-sm bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-400 cursor-not-allowed flex items-center gap-2 text-sm font-medium h-10"
      >
        <Plus className="w-4 h-4" />
        No buttons available
      </button>
    );
  }

  const renderOption = (type: ButtonType, isCta: boolean) => {
    const IconComponent = type.icon;
    const isDisabled = type.disabled;
    const isHovered = hoveredValue === type.value;

    return (
      <button
        key={type.value}
        type="button"
        onMouseEnter={() => !isDisabled && setHoveredValue(type.value)}
        onMouseLeave={() => setHoveredValue(null)}
        onClick={() => !isDisabled && handleSelect(type.value)}
        disabled={isDisabled}
        data-testid={testId ? `${testId}-option-${type.value}` : undefined}
        className={`w-full flex items-center gap-3 px-4 py-3 transition-all text-left group relative ${isDisabled
          ? 'opacity-50 cursor-not-allowed grayscale'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
          }`}
      >
        <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${isDisabled ? 'bg-gray-100 dark:bg-gray-800' : isCta ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-blue-50 dark:bg-blue-900/20'
          }`}>
          <IconComponent className={`w-4 h-4 ${isDisabled ? 'text-gray-400' : isCta ? 'text-purple-600 dark:text-purple-400' : 'text-blue-600 dark:text-blue-400'
            }`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold truncate ${isDisabled ? 'text-gray-500' : 'text-gray-900 dark:text-white group-hover:text-green-600 dark:group-hover:text-green-400'
            }`}>
            {type.label}
          </div>
          {isDisabled && type.disabledReason ? (
            <div className="text-[10px] leading-tight text-red-500 font-medium mt-0.5">
              {type.disabledReason}
            </div>
          ) : type.sublabel ? (
            <div className="text-[10px] leading-tight text-gray-400 dark:text-gray-400 mt-0.5 group-hover:text-gray-500 dark:group-hover:text-gray-400">
              {type.sublabel}
            </div>
          ) : null}
        </div>

        {!isDisabled && isHovered && (
          <ArrowRight className="w-4 h-4 text-green-500 animate-in fade-in slide-in-from-left-2 duration-200" />
        )}
      </button>
    );
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled || isAllDisabled}
        data-testid={testId}
        className={`px-4 py-2 border rounded-sm bg-white dark:bg-gray-800 text-left text-gray-900 dark:text-white transition-all flex items-center justify-between gap-3 text-sm font-semibold shadow-sm h-10 ${disabled || isAllDisabled
          ? 'opacity-50 border-gray-200 dark:border-gray-700 cursor-not-allowed'
          : isOpen
            ? 'border-green-500 ring-4 ring-green-100 dark:ring-green-900/30'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 active:scale-[0.98]'
          }`}
      >
        <div className="flex items-center gap-2">
          <Plus className={`w-4 h-4 ${isAllDisabled ? 'text-gray-400' : 'text-green-600 dark:text-green-400'}`} />
          <span>{isAllDisabled ? 'Max reached' : 'Add button'}</span>
        </div>
        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className={`absolute left-0 sm:left-auto sm:right-0 bg-white dark:bg-gray-800 rounded-xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.3)] border border-gray-100 dark:border-gray-700 overflow-hidden z-[50] w-[min(300px,calc(100vw-1rem))] animate-in fade-in duration-300 ${
            dropUp 
              ? 'bottom-full mb-4 slide-in-from-bottom-2' 
              : 'top-full mt-4 slide-in-from-top-2'
          }`}
        >
          {/* Custom Section Header */}
          {customTypes.length > 0 && (
            <div className="px-4 py-2 bg-gray-50/50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-400 uppercase tracking-widest">
                Quick Reply
              </span>
            </div>
          )}

          {/* Custom Section */}
          <div className="py-1">
            {customTypes.map(type => renderOption(type, false))}
          </div>

          {/* Call to Action Section Header */}
          {ctaTypes.length > 0 && (
            <div className="px-4 py-2 bg-gray-50/50 dark:bg-gray-900/50 border-t border-b border-gray-100 dark:border-gray-700">
              <span className="text-[10px] font-bold text-gray-400 dark:text-gray-400 uppercase tracking-widest">
                Call to Action
              </span>
            </div>
          )}

          {/* Call to Action Section */}
          <div className="py-1">
            {ctaTypes.map(type => renderOption(type, true))}
          </div>
        </div>
      )}
    </div>
  );
};
